import { Input, SegmentedControl } from '@aecom-ai-engineering/react-components';
import { useProjectId } from '@consigli/hooks';
import { MailPlus, Search as SearchIcon, TextSearch, Users as UsersIcon } from 'lucide-react';
import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/empty-state';

import { ProjectInvitesTable } from './project-invites-table';
import { ProjectOverviewError } from './project-overview-error';
import { ProjectUsersTable } from './project-users-table';
import { PEOPLE_TAB, useProjectPeople } from './use-project-people';

interface PeopleEmptyProps {
  onUsers: boolean;
  /** Non-empty when the list is empty because the search matched nothing. */
  searchText: string;
}

/**
 * What replaces the table when there are no rows.
 *
 * Four cases across the two sub-tabs, none of them drawn in Figma, so they
 * follow the project list's empty states: a search that matched nothing keeps
 * its own copy and icon, because offering to invite someone is the wrong answer
 * to a typo, and the genuinely-empty case carries the backdrop the redesign uses
 * elsewhere.
 *
 * No invite action on the empty Invites panel. The header's Invite Users button
 * is three inches above it and goes to the only invite flow there is.
 */
const PeopleEmpty: FC<PeopleEmptyProps> = ({ onUsers, searchText }) => {
  const { t } = useTranslation();

  if (searchText) {
    return (
      <EmptyState
        announce
        backdrop
        description={t(
          onUsers ? 'project-overview.no-users-found' : 'project-overview.no-invites-found',
        )}
        icon={<TextSearch size={24} strokeWidth={1.75} />}
        title={t('project-overview.no-results-title')}
      />
    );
  }

  return (
    <EmptyState
      backdrop
      description={t(onUsers ? 'project-overview.no-users' : 'project-overview.no-invites')}
      icon={
        onUsers ? (
          <UsersIcon size={24} strokeWidth={1.75} />
        ) : (
          <MailPlus size={24} strokeWidth={1.75} />
        )
      }
    />
  );
};

/**
 * The Users & Invites tab body: the project's members, and the invites still
 * out on it.
 *
 * `SegmentedControl` at `variant="pill"` for the sub-tabs, which is the design
 * system's own rendering of the control the frame calls "Tabs". It deliberately
 * carries no tab semantics, so the two lists are not `role="tabpanel"`. Each
 * table names itself through its caption instead.
 *
 * The sub-tab lives in a query param and the search term does not — see
 * `useProjectPeople` for why.
 */
export const UsersTab: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const {
    tab,
    setTab,
    searchText,
    handleSearch,
    isSearchPending,
    users,
    invites,
    isLoading,
    isError,
    isRetrying,
    refetch,
  } = useProjectPeople(projectId);

  const onUsers = tab === PEOPLE_TAB.USERS;
  const rows = onUsers ? users : invites;

  const body = ((): ReactNode => {
    // isSearchPending as well as isLoading: between the keystroke and the
    // debounce firing the list still holds the previous term's rows, and
    // leaving them up reads as a search that matched the wrong thing.
    if (isLoading || isSearchPending) {
      return onUsers ? (
        <ProjectUsersTable isLoading projectId={projectId} users={[]} />
      ) : (
        <ProjectInvitesTable invites={[]} isLoading />
      );
    }

    // The same panel the route shows when the project itself fails to load.
    // A people list that will not load is the retryable arm and never the
    // no-access one: the route already resolved the project, so reaching this
    // tab at all means access was granted.
    if (isError) {
      return (
        <ProjectOverviewError isRetrying={isRetrying} onRetry={refetch} variant="unavailable" />
      );
    }

    if (rows.length === 0) {
      return <PeopleEmpty onUsers={onUsers} searchText={searchText} />;
    }

    return onUsers ? (
      <ProjectUsersTable projectId={projectId} users={users} />
    ) : (
      <ProjectInvitesTable invites={invites} />
    );
  })();

  return (
    // gap-4 is the frame's 16 between the toolbar and the table.
    //
    // py-8 and not the frame's 16/40: the Agents tab's frame draws 32/32, and
    // two sibling tabs inside one shell cannot disagree about their top edge
    // without the content jumping every time you switch tab. Raised upstream.
    <div className="flex flex-col gap-4 py-8">
      <div className="flex items-center justify-between gap-4">
        <SegmentedControl
          items={[
            { label: t('project-overview.users'), value: PEOPLE_TAB.USERS },
            { label: t('project-overview.invites'), value: PEOPLE_TAB.INVITES },
          ]}
          label={t('project-overview.people-tabs-label')}
          onValueChange={({ value }) =>
            setTab(value[0] === PEOPLE_TAB.INVITES ? PEOPLE_TAB.INVITES : PEOPLE_TAB.USERS)
          }
          value={[tab]}
          variant="pill"
        />

        {/* w-60 is the frame's 240px; Input puts className on its shell.
            type="search" keeps the browser's native clear affordance. Both as
            the project list's toolbar sets them. */}
        <Input
          className="w-60"
          icon={SearchIcon}
          label={t('project-overview.search-people')}
          onChange={handleSearch}
          placeholder={t('project-overview.search-people')}
          size="md"
          type="search"
          value={searchText}
        />
      </div>

      {body}
    </div>
  );
};
